import type { TranslationKeys } from '@/i18n/types';

export const en: TranslationKeys = {
  common: {
    loading: 'Loading...',
    error: 'Something went wrong',
    retry: 'Try again',
    save: 'Save',
    cancel: 'Cancel',
    continue: 'Continue',
    skip: 'Skip',
    back: 'Back',
    next: 'Next',
    close: 'Close',
    search: 'Search',
    settings: 'Settings',
  },
  greeting: {
    morning: 'Good morning',
    afternoon: 'Good afternoon',
    evening: 'Good evening',
  },
  auth: {
    signIn: 'Sign in',
    signOut: 'Sign out',
    signUp: 'Sign up',
    continueWith: 'Continue with {{provider}}',
    orContinueWith: 'or continue with',
    magicLink: 'Sign in with email',
    magicLinkSent: 'Link sent! Check your email.',
    sentTo: 'We sent a link to {{email}}',
    emailPlaceholder: 'your@email.com',
    errors: {
      invalidEmail: 'Invalid email',
      generic: 'Authentication error. Try again.',
      rateLimited: 'Too many attempts. Please wait a minute.',
      invalidCredentials: 'Invalid email or password.',
    },
  },
  onboarding: {
    welcome: 'Welcome to Newsletter',
    step1Title: "What's your profile?",
    step1Subtitle: 'Select one or more profiles that best describe you.',
    step2Title: 'Tell us more about you',
    step2Subtitle: 'This information is optional and helps personalize your feed.',
    profiles: {
      engineer: 'Engineer',
      engineer_desc: 'Development, architecture, DevOps',
      product_manager: 'Product Manager',
      product_manager_desc: 'Product management, strategy',
      designer: 'Designer',
      designer_desc: 'UX, UI, product design',
      founder: 'Founder / Leader',
      founder_desc: 'Entrepreneur, C-level, leadership',
      generalist: 'Generalist',
      generalist_desc: 'Broad interest in technology',
    },
    role: 'Role',
    rolePlaceholder: 'Select your role',
    industry: 'Industry',
    industryPlaceholder: 'Select your industry',
    letsGo: "Let's go!",
  },
  feed: {
    title: 'Feed',
    empty: 'Nothing new here',
    emptySubtitle: 'Come back later for new reads.',
    endOfFeed: "That's all for today",
    endOfFeedSubtitle: "You're all caught up.",
    slowDay: 'Slow day today',
    slowDaySubtitle: 'How about revisiting some classics?',
    pullToRefresh: 'Pull to refresh',
    refreshing: 'Refreshing...',
  },
  content: {
    readOriginal: 'Read original',
    readInPortuguese: 'Read in Portuguese',
    share: 'Share',
    shareOn: 'Share on',
    copyLink: 'Copy link',
    linkCopied: 'Link copied!',
    readingTime: '{{minutes}} min read',
    previous: 'Previous',
    next: 'Next',
  },
  actions: {
    like: 'Useful',
    liked: 'Marked as useful',
    save: 'Save',
    saved: 'Saved',
    dismiss: 'Skip',
    dismissed: 'Skipped',
    more: 'More',
    lessLikeThis: 'Less like this',
    muteAuthor: 'Mute author',
    muteSource: 'Mute source',
    undoSave: 'Undo',
    removed: 'Removed',
  },
  saved: {
    title: 'Saved',
    empty: 'Nothing saved yet',
    emptySubtitle: 'Save articles to read later.',
    savedAgo: 'Saved {{time}} ago',
    remove: 'Remove',
  },
  time: {
    now: 'Now',
    hoursAgo: '{{count}}h',
    daysAgo: '{{count}}d',
    weeksAgo: '{{count}}w',
    monthsAgo: '{{count}}mo',
  },
  errors: {
    network: 'Connection error. Check your internet.',
    notFound: 'Content not found',
    unauthorized: 'You need to be logged in',
    generic: 'Something went wrong. Try again.',
  },
  landing: {
    heroTitle: 'What you\nneed to read about\ntechnology.',
    heroSubtitle: 'The essentials, every day. Curation powered by technology.',
    heroButton: 'Read now',
    ctaSecondary: 'How it works',
    whyTitle: 'Why Newsletter?',
    whySubtitle: 'Three reasons that make Newsletter the best way to keep up with the tech world.',
    feature1Title: 'We search',
    feature1Desc:
      'We manually select the best tech content. Each article is chosen by experts who understand what really matters to you.',
    feature2Title: 'We filter',
    feature2Desc:
      'Our platform learns from your preferences. The more you read, the more personalized your content discovery experience becomes.',
    feature3Title: 'The essentials',
    feature3Desc:
      'Take your reads anywhere. Save articles and access them even offline, turning any moment into a learning opportunity.',
    ctaTitle: 'Ready to get started?',
    ctaButton: 'Create free account',
    footerRights: 'All rights reserved.',
  },
  consent: {
    title: 'Your privacy',
    description:
      'We use analytics cookies to understand how you interact with Newsletter and improve your experience. Your data is handled according to our',
    accept: 'Accept',
    decline: 'Decline',
    privacyLink: 'Privacy Policy',
  },
  pwa: {
    title: 'Install Newsletter',
    description: 'Add to your home screen for quick access and offline experience.',
    install: 'Install',
    dismiss: 'Not now',
  },
  themes: {
    ai_ml: 'AI / Machine Learning',
    engineering: 'Engineering',
    product: 'Product',
    design: 'Design',
    startup: 'Startup',
    leadership: 'Leadership',
    career: 'Career',
  },
  newsletter: {
    title: 'Get the best of tech',
    subtitle: 'Daily curation delivered to your inbox. No spam, just the essentials.',
    placeholder: 'your@email.com',
    subscribe: 'Subscribe',
    subscribing: 'Subscribing...',
    success: 'Subscription confirmed! Check your inbox.',
    alreadySubscribed: 'This email is already subscribed.',
    error: 'Failed to subscribe. Please try again.',
    invalidEmail: 'Invalid email',
  },
  admin: {
    title: 'Content Review',
    backToFeed: 'Feed',
    filters: {
      status: {
        pending: 'Pending',
        reviewed: 'Reviewed',
        flagged: 'Flagged',
        newsletter: 'Newsletter',
        all: 'All',
      },
      period: 'Period',
      periodOptions: {
        all: 'All',
        day: '24h',
        week: '7 days',
        month: '30 days',
      },
      confidence: 'Confidence',
    },
    summary: {
      total: 'Total',
      items: 'items',
      reviewed: 'reviewed',
      pending: 'pending',
      flagged: 'flagged',
    },
    list: {
      searchPlaceholder: 'Search content...',
      noContent: 'No content found',
      revisions: 'rev',
      noDate: 'No date',
      showingAll: 'Showing all {{count}} items',
    },
    detail: {
      selectContent: 'Select content to review',
      contentNotFound: 'Content not found',
      language: 'Language',
      revisionsCount: 'Revisions',
      humanReviewed: 'Human reviewed',
      edit: 'Edit',
      resetToAI: 'Reset to AI',
    },
    fields: {
      title: 'Title',
      summary: 'Summary',
      category: 'Category',
      author: 'Author',
      tags: 'Tags',
      theme: 'Theme',
      themeSuggested: 'Theme (suggested)',
      nature: 'Nature',
      depth: 'Depth',
      timeliness: 'Timeliness',
      originality: 'Originality',
      authorityScore: 'Authority',
      densityScore: 'Density',
      primarySubject: 'Subject',
      mentions: 'Mentions',
      modified: 'Modified',
      generated: 'Generated',
      current: 'Current',
      notGenerated: 'Not generated',
      noTagsGenerated: 'No tags generated',
      noTagsSelected: 'No tags selected',
      suggested: 'suggested',
      notClassified: 'Not classified',
    },
    classification: {
      title: 'Classification',
      theme: 'Theme',
      nature: 'Nature',
      depth: 'Depth',
      timeliness: 'Timeliness',
      originality: 'Originality',
    },
    quality: {
      title: 'Quality Signals',
      authorityScore: 'Authority Score',
      authorityDesc: 'Author/source credibility',
      densityScore: 'Density Score',
      densityDesc: 'Information density',
    },
    subjects: {
      title: 'Subjects',
      primary: 'Primary Subject',
      mentions: 'Mentions',
      notIdentified: 'Not identified',
      noneIdentified: 'None identified',
    },
    revisionHistory: {
      title: 'Revision History',
      noFields: 'No fields',
    },
    fullContent: {
      title: 'Full Content',
      noContent: 'No content available',
    },
    content: {
      title: 'Article Content',
      rendered: 'Rendered',
      html: 'HTML',
    },
    comments: {
      title: 'Comments',
      placeholder: 'Add a comment...',
      noComments: 'No comments yet',
    },
    status: {
      approved: 'Approved',
      pending: 'Pending',
      rejected: 'Rejected',
      flagged: 'Flagged',
      notReviewed: 'Not reviewed',
      reviewedAt: 'Reviewed: {{date}}',
    },
    actions: {
      saveChanges: 'Save Changes',
      approve: 'Approve',
      reject: 'Reject',
      flag: 'Flag',
      unflag: 'Unflag',
      flagTooltip: 'Flag content (hide from feed)',
      unflagTooltip: 'Unflag content',
      translate: 'Translate to pt-BR',
      translateTooltip: 'Generate a Portuguese version of this content',
    },
    newsletter: {
      viewLink: 'View newsletter',
      sentAt: 'Sent',
      recipients: 'recipients',
      sourceArticles: 'Source Articles',
    },
  },
};
